import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useLanguage from '../hooks/useLanguage';
import { repositories } from '../data/repositories';

const { FiSearch, FiExternalLink, FiList, FiX } = FiIcons;

const processedRepositories = repositories.map(repo => ({
  ...repo,
  normalizedName: repo.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}));

const searchIndex = processedRepositories.reduce((acc, repo) => {
  const words = repo.normalizedName.split(/\s+/);
  words.forEach(word => {
    if (word.length > 2) {
      if (!acc[word]) acc[word] = [];
      acc[word].push(repo);
    }
  });
  return acc;
}, {});

const SearchSection = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAlphabetical, setShowAlphabetical] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);
  const searchCache = useRef(new Map());

  const filteredRepositories = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const normalizedSearchTerm = searchTerm
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    if (searchCache.current.has(normalizedSearchTerm)) {
      return searchCache.current.get(normalizedSearchTerm);
    }

    let results = [];
    const seenIds = new Set();

    if (searchIndex[normalizedSearchTerm]) {
      searchIndex[normalizedSearchTerm].forEach(repo => {
        if (!seenIds.has(repo.id)) {
          results.push(repo);
          seenIds.add(repo.id);
        }
      });
    }

    const searchWords = normalizedSearchTerm.split(/\s+/).filter(w => w.length > 2);
    if (searchWords.length > 0) {
      searchWords.forEach(word => {
        if (searchIndex[word]) {
          searchIndex[word].forEach(repo => {
            if (!seenIds.has(repo.id)) {
              results.push(repo);
              seenIds.add(repo.id);
            }
          });
        }
      });
    }

    processedRepositories.forEach(repo => {
      if (!seenIds.has(repo.id) && repo.normalizedName.includes(normalizedSearchTerm)) {
        results.push(repo);
        seenIds.add(repo.id);
      }
    });

    results.sort((a, b) => {
      const aExact = a.normalizedName === normalizedSearchTerm;
      const bExact = b.normalizedName === normalizedSearchTerm;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return a.normalizedName.localeCompare(b.normalizedName);
    });

    results = results.slice(0, 50);
    searchCache.current.set(normalizedSearchTerm, results);
    return results;
  }, [searchTerm]);

  const alphabeticalRepositories = useMemo(() => {
    return [...repositories].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, []);

  const handleRepositoryClick = useCallback((url, event) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (!searchTerm.trim() || filteredRepositories.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < filteredRepositories.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (!showResults) {
          setShowResults(true);
        } else if (selectedIndex >= 0 && selectedIndex < filteredRepositories.length) {
          handleRepositoryClick(filteredRepositories[selectedIndex].url, e);
        }
        break;
      case 'Escape':
        setSelectedIndex(-1);
        setShowResults(false);
        searchInputRef.current?.blur();
        break;
    }
  }, [searchTerm, filteredRepositories, showResults, selectedIndex, handleRepositoryClick]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
    setShowResults(!!value.trim());
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    setSelectedIndex(-1);
    setShowResults(false);
    searchInputRef.current?.focus();
  }, []);

  return (
    <motion.section 
      className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-8 sm:py-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
          {t('searchTitle')}
        </h2>
        <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {t('searchSubtitle')}
        </p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        <div className="relative">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg sm:text-xl pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('placeholder')}
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 text-sm sm:text-lg border-2 border-gray-200 dark:border-gray-600 rounded-xl sm:rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-0 transition-all duration-200 shadow-lg hover:shadow-xl"
            />
            {searchTerm && (
              <button onClick={handleClear} className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <SafeIcon icon={FiX} className="text-lg sm:text-xl" />
              </button>
            )}
          </div>

          <AnimatePresence>
            {showResults && searchTerm && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl sm:rounded-2xl shadow-2xl z-30 max-h-64 sm:max-h-96 overflow-y-auto"
              >
                {filteredRepositories.length > 0 ? (
                  <div className="p-2">
                    {filteredRepositories.map((repo, index) => (
                      <div
                        key={repo.id}
                        onClick={(e) => handleRepositoryClick(repo.url, e)}
                        className={`w-full p-3 sm:p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg sm:rounded-xl transition-all duration-200 group flex items-center justify-between cursor-pointer ${selectedIndex === index ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                      >
                        <span className="text-sm sm:text-base text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate flex-1 mr-2">
                          {repo.name}
                        </span>
                        <SafeIcon icon={FiExternalLink} className="text-gray-400 group-hover:text-blue-500 transition-all duration-200 flex-shrink-0 text-sm sm:text-base" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                    {t('noResults', { searchTerm })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex justify-center">
          <motion.button
            onClick={() => setShowAlphabetical(!showAlphabetical)}
            className="inline-flex items-center space-x-2 sm:space-x-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={showAlphabetical ? FiX : FiList} className="text-lg sm:text-xl" />
            <span className="font-medium">
              {showAlphabetical ? t('hideList') : t('showList')}
            </span>
          </motion.button>
        </div>

        <AnimatePresence>
          {showAlphabetical && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-xl border border-gray-200 dark:border-gray-600 overflow-hidden"
            >
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6 text-center">
                  {t('listTitle')}
                </h3>
                <div className="grid gap-2 max-h-64 sm:max-h-96 overflow-y-auto pr-2">
                  {alphabeticalRepositories.map((repo, index) => (
                    <motion.div
                      key={repo.id}
                      onClick={(e) => handleRepositoryClick(repo.url, e)}
                      className="p-3 sm:p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg sm:rounded-xl transition-all duration-200 group flex items-center justify-between cursor-pointer"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.005 }}
                      whileHover={{ x: 5 }}
                    >
                      <div className="flex items-center flex-1 mr-2 min-w-0">
                        <span className="text-xs sm:text-sm font-mono text-blue-500 mr-3 w-8 flex-shrink-0 text-right">
                          {(index + 1).toString().padStart(3, '0')}
                        </span>
                        <span className="text-sm sm:text-base text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                          {repo.name}
                        </span>
                      </div>
                      <SafeIcon icon={FiExternalLink} className="text-gray-400 group-hover:text-blue-500 transition-all duration-200 flex-shrink-0 text-sm sm:text-base" />
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
};

export default SearchSection;